/**
 * SA-480 (spec 2026-09-25 D4): the ONE rule for a link to a task page from a
 * record-lane read. Those reads (the register, the record view) show every
 * task on a record the viewer can see, while the task page is seat-bound
 * (SA-376): the server says whether THIS viewer can open each task
 * (`TaskRead.can_open`, `OrderProcessSummary.openTaskCanOpen`, the task
 * page's own rule), and a task they cannot open becomes its step — the
 * record's step drawer, which any record-visible viewer can open. Each site
 * words the `step` fallback where it sits; none re-derives the rule.
 */
import type { TaskRead } from '@/types/process'

export type TaskTarget = { kind: 'task'; to: string } | { kind: 'step'; stepId: string }

export const taskPath = (id: string): string => `/tasks/${id}`

export function taskTarget(t: { id: string; canOpen: boolean; stepId: string }): TaskTarget {
  return t.canOpen ? { kind: 'task', to: taskPath(t.id) } : { kind: 'step', stepId: t.stepId }
}

export const taskTargetOf = (task: Pick<TaskRead, 'id' | 'can_open' | 'step_id'>): TaskTarget =>
  taskTarget({ id: task.id, canOpen: task.can_open, stepId: task.step_id })

/** `?step=` beside `?order=` is the SA-460 step drawer, never the register's step filter (SA-470). */
export const recordStepHref = (orderId: string, stepId: string): string =>
  `/orders?${new URLSearchParams({ order: orderId, step: stepId }).toString()}`

/** SA-1078 (UX pass spec move 3, Q4): where a task page was opened from, carried
 * in router state as `{ from: TaskOrigin }` — never a query parameter, so a
 * shared `/tasks/<id>` link opens on the task. `to` is the page to go back to
 * (its own path and query, so Back lands on the drawer or section the person
 * had open); `label` is the crumb's words, absent on a record-lane site, where
 * the task page words it as the task's record. */
export interface TaskOrigin {
  to: string
  label?: string
  /** SA-1338: how many history entries back `to` sits from the task page. Every opener
   * pushes the task page from `to`, so it is 1; the re-open path adds one. */
  back?: number
  /** The page load that made `back`: after a reload the entries behind the page belong
   * to the earlier document, so `readOrigin` keeps `back` only for this load. */
  load?: string
}

/** Made once per module load, which is once per page load in the browser. */
export const PAGE_LOAD: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36)

export const ORIGIN_MY_WORK = 'My work'
export const ORIGIN_HOME = 'Home'

export const taskLinkState = (origin: TaskOrigin): { from: TaskOrigin } => ({ from: origin })

/** The current page as an origin: pathname + search, as `useLocation()` reports them. */
export function fromHere(location: { pathname: string; search: string }, label?: string): TaskOrigin {
  const origin: TaskOrigin = { to: `${location.pathname}${location.search}` }
  if (label) origin.label = label
  origin.back = 1
  origin.load = PAGE_LOAD
  return origin
}

/** The same origin seen from a page one entry further on (a re-opened task replaces
 * nothing: it is pushed beside the one it came from). */
export function oneFurther(origin: TaskOrigin): TaskOrigin {
  if (origin.back === undefined) {
    const { back: _back, load: _load, ...rest } = origin
    return rest
  }
  return { ...origin, back: origin.back + 1 }
}
